import React, { useCallback, useEffect, useState } from 'react';
import BASE_URL from '../config';

const box = { color: '#e5e7eb' };
const btn = (bg = '#2a3342') => ({
  background: bg, color: '#fff', border: '1px solid #3b4658', borderRadius: 6,
  padding: '6px 12px', cursor: 'pointer', fontSize: 13, marginRight: 8,
});
const th = { textAlign: 'left', padding: '6px 10px', borderBottom: '1px solid #3b4658', color: '#9ca3af', fontSize: 12 };
const td = { padding: '6px 10px', borderBottom: '1px solid #232c3a', fontSize: 13 };

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('jwt_token')}`,
});

/**
 * Admin panel for a case's log files: uploaded logs on the server, plus
 * remote control of the files stored on the case itself (list / request / erase
 * via the log_upload C2D commands).
 */
const CaseLogsPanel = ({ caseUid, showUploaded = true, onChange }) => {
  const [uploaded, setUploaded] = useState([]);
  const [loadingUploaded, setLoadingUploaded] = useState(false);
  const [deviceFiles, setDeviceFiles] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  const fetchUploaded = useCallback(async () => {
    if (!caseUid || !showUploaded) return;
    setLoadingUploaded(true);
    try {
      const r = await fetch(`${BASE_URL}/api/pocket/admin/logs/?case_uid=${encodeURIComponent(caseUid)}`, { headers: authHeaders() });
      setUploaded(r.ok ? await r.json() : []);
    } catch (e) {
      setUploaded([]);
    } finally {
      setLoadingUploaded(false);
    }
  }, [caseUid, showUploaded]);

  useEffect(() => { fetchUploaded(); }, [fetchUploaded]);

  const command = async (event, extra = {}) => {
    setBusy(true);
    setMessage(`Sending "${event}" to case… (waits up to 15 s)`);
    try {
      const r = await fetch(`${BASE_URL}/api/pocket/admin/logs/command/`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ case_uid: caseUid, event, ...extra }),
      });
      const data = await r.json();
      if (data.status === 'SENT_NO_REPLY') {
        setMessage('Command delivered but the case did not reply (asleep or offline?).');
        return null;
      }
      if (!r.ok) {
        setMessage(data.error || 'Command failed.');
        return null;
      }
      return data.reply || null;
    } catch (e) {
      setMessage('Request failed.');
      return null;
    } finally {
      setBusy(false);
    }
  };

  const listOnCase = async () => {
    const reply = await command('list');
    if (!reply) return;
    if (reply.result === 'PASS') {
      setDeviceFiles(reply.value?.files || []);
      setMessage(`Case has ${reply.value?.total ?? 0} log file(s).`);
    } else {
      setMessage(`Case replied: ${JSON.stringify(reply)}`);
    }
  };

  const requestFile = async (file) => {
    const reply = await command('request', { file, description: 'Requested from console' });
    if (!reply) return;
    const st = reply.value?.state;
    if (st === 'uploading') {
      setMessage(`Uploading ${file}… refresh the uploaded list in a few seconds.`);
      setTimeout(() => { fetchUploaded(); if (onChange) onChange(); }, 8000);
    } else if (st === 'busy') {
      setMessage('Case is busy with another upload. Try again shortly.');
    } else {
      setMessage(`Case replied: ${st || 'error'}${reply.value?.err ? ' – ' + reply.value.err : ''}`);
    }
  };

  const eraseOnCase = async () => {
    if (!window.confirm('Erase ALL log files stored on the case? Files already uploaded to the server are kept.')) return;
    const reply = await command('erase');
    if (!reply) return;
    if (reply.result === 'PASS') {
      setDeviceFiles([]);
      setMessage(`Erased ${reply.value?.deleted ?? 0} file(s) on the case.`);
    } else {
      setMessage(`Case replied: ${JSON.stringify(reply)}`);
    }
  };

  if (!caseUid) return <p style={{ color: '#888' }}>No case selected.</p>;

  return (
    <div style={box}>
      <div style={{ marginBottom: 12 }}>
        <span style={{ color: '#9ca3af', fontSize: 13 }}>Case UID: {caseUid}</span>
      </div>

      <div style={{ marginBottom: 12 }}>
        <button style={btn()} disabled={busy} onClick={listOnCase}>List files on case</button>
        <button style={btn('#6b2a2a')} disabled={busy} onClick={eraseOnCase}>Erase files on case</button>
        {showUploaded && <button style={btn()} onClick={fetchUploaded}>Refresh uploaded</button>}
      </div>
      {message && <p style={{ color: '#f59e0b', fontSize: 13, margin: '0 0 12px' }}>{message}</p>}

      {deviceFiles && (
        <div style={{ marginBottom: 20 }}>
          <h3 style={{ color: '#fff', fontSize: 15, margin: '0 0 6px' }}>Files on the case</h3>
          {deviceFiles.length === 0 ? <p style={{ color: '#888' }}>None.</p> : (
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead><tr><th style={th}>#</th><th style={th}>File</th><th style={th}></th></tr></thead>
              <tbody>
                {deviceFiles.map(f => (
                  <tr key={f.n}>
                    <td style={td}>{f.seq}</td>
                    <td style={td}>{f.n}</td>
                    <td style={td}><button style={btn()} disabled={busy} onClick={() => requestFile(f.n)}>Request upload</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {showUploaded && (
        <div>
          <h3 style={{ color: '#fff', fontSize: 15, margin: '0 0 6px' }}>Uploaded to server</h3>
          {loadingUploaded ? <p>Loading…</p> : uploaded.length === 0 ? <p style={{ color: '#888' }}>No case log files uploaded yet.</p> : (
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead><tr><th style={th}>Uploaded</th><th style={th}>Description</th><th style={th}>File</th></tr></thead>
              <tbody>
                {uploaded.map(l => (
                  <tr key={l.id}>
                    <td style={td}>{l.received_at ? new Date(l.received_at).toLocaleString() : '-'}</td>
                    <td style={td}>{l.description || '-'}</td>
                    <td style={td}>{l.log_file_url ? <a style={{ color: '#4da3ff' }} href={l.log_file_url} target="_blank" rel="noopener noreferrer">{l.file_name || 'Download'}</a> : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default CaseLogsPanel;
